import React, { useEffect, useState } from 'react'

import { successMessages } from '@/constants/feedbackMessages'
import {
	AutocompleteBase,
	Button,
	CardStats,
	ChartDoughnut,
	ChartsHistoric,
	ClearButton,
	FilterButton,
	ModalBase,
	Paragraph,
	RefreshButton,
	SelectBase,
	SideModalBase,
	TableHistoric,
	TagsActions,
	Title,
} from '@components/atoms'
import { CookieStorage } from '@helpers'
import { useIsMobile } from '@hooks'
import useQuestions from '@hooks/useQuestion'
import type { DashBoardResponse } from '@models/dashboard.model'
import { dashboardAPI } from '@services/DashboardAPI'
import { questionsAPI } from '@services/QuestionsAPI'
import { modalCreators } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import moment from 'moment'

import { DashboardStyles } from './style'

const {
	listCardsStats,
	container,
	title,
	buttons,
	listCharts,
	filterSideMenu,
	tagsSideMenu,
	buttonSideMenu,
	deleteHistoryContainer,
	deleteHistoryImage,
	deleteHistoryTitle,
	deleteHistoryParaghaph,
	deleteHistoryInputText,
	deleteHistoryButton,
} = DashboardStyles

type InitOptionsProps = {
	id: number
	name: string
	value: string | number
}

export type AllInitOptionsProps = {
	articles: Array<InitOptionsProps>
	disciplines: Array<InitOptionsProps>
	institutions: Array<InitOptionsProps>
	materId: Array<InitOptionsProps>
	subMaterId: Array<InitOptionsProps>
	juries: Array<InitOptionsProps>
	roles: Array<InitOptionsProps>
	status: Array<InitOptionsProps>
	years: Array<InitOptionsProps>
}

export type IntervaDatelProps = {
	dateInterval: { value: string; label: string }
}

export const Dashboard = () => {
	const [dashValues, setDashValues] = useState<DashBoardResponse>({
		answered: 0,
		correct: 0,
		errors: 0,
		totalAnswered: 0,
		history: [],
		lastMockResolutions: [],
	})
	const [initOptions, setInitOptions] = useState<AllInitOptionsProps>({
		articles: [],
		disciplines: [],
		institutions: [],
		materId: [],
		subMaterId: [],
		juries: [],
		roles: [],
		status: [],
		years: [],
	})
	const [interval, setInterval] = useState<IntervaDatelProps>({
		dateInterval: { label: 'Período', value: 'all' },
	})
	const [openMenufilter, setOpenMenuFilter] = useState(false)
	const [openModalClear, setOpenModalClear] = useState(false)

	const { setValues, values, clearFilter } = useQuestions()

	const [inputValue, setInputValue] = useState('')

	const dispatch = useAppDispatch()
	const mobile = useIsMobile()

	const [GetDashboard, { isSuccess, data }] =
		dashboardAPI.useGetDashboardFilteredMutation()

	const [ClearHistoric, { isSuccess: isSuccessClearHistoric }] =
		dashboardAPI.useClearHistoricMutation()

	const [
		GetAllOptionsFilter,
		{ isSuccess: isSuccessInitOptions, data: dataInitOptions },
	] = questionsAPI.useGetAllOptionsFilterMutation()

	const [getOptionsMatter, { data: dataMatter, isSuccess: isSuccessMatter }] =
		questionsAPI.useGetOptionMatterMutation()

	const [
		getOptionsSubMatter,
		{ data: dataSubMatter, isSuccess: isSuccessSubMatter },
	] = questionsAPI.useGetOptionSubMatterMutation()

	const handleUpdateDash = () => {
		GetDashboard({
			disciplineIds: values.disciplines.map((value) => value.id).join(','),
			matterIds: values.materId.map((value) => value.id).join(','),
			submatterIds: values.subMaterId.map((value) => value.id).join(','),
			yearIds: values.year.map((value) => value.id).join(','),
			positionIds: values.office.map((value) => value.id).join(','),
			juryIds: values.juries.map((value) => value.id).join(','),
			institutionIds: values.institutions.map((value) => value.id).join(','),
			dateInterval: interval.dateInterval.value,
		})
		setOpenMenuFilter(false)
	}

	const { data: userInfo } = dashboardAPI.useListMeQuery()

	const cookiesStorage = new CookieStorage()

	const handleClear = () => {
		ClearHistoric()
	}

	const isButtonDisabled = inputValue !== 'Apagar histórico'

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(event.target.value)
	}

	const handleClearFilter = () => {
		setInterval({
			dateInterval: { label: 'Período', value: 'all' },
		})
		clearFilter()
	}

	useEffect(() => {
		GetDashboard({
			disciplineIds: '',
			matterIds: '',
			submatterIds: '',
			yearIds: '',
			positionIds: '',
			juryIds: '',
			institutionIds: '',
			dateInterval: interval.dateInterval.value,
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [GetDashboard])

	useEffect(() => {
		GetAllOptionsFilter()
	}, [GetAllOptionsFilter])

	useEffect(() => {
		if (isSuccess && data) {
			setDashValues(data)
		}
	}, [isSuccess, data])

	useEffect(() => {
		if (isSuccessInitOptions && dataInitOptions) {
			setInitOptions(dataInitOptions)
		}
	}, [isSuccessInitOptions, dataInitOptions])

	useEffect(() => {
		if (values.disciplines.length > 0) {
			getOptionsMatter(values.disciplines.map((value) => value.id).join(','))
		}
	}, [values.disciplines, getOptionsMatter])

	useEffect(() => {
		if (isSuccessMatter && dataMatter) {
			setInitOptions((prevState) => ({
				...prevState,
				materId: dataMatter.data,
			}))
		}
	}, [isSuccessMatter, dataMatter])

	useEffect(() => {
		if (values.materId.length > 0) {
			getOptionsSubMatter(values.materId.map((value) => value.id).join(','))
		}
	}, [values.materId, getOptionsSubMatter])

	useEffect(() => {
		if (isSuccessSubMatter && dataSubMatter) {
			setInitOptions((prevState) => ({
				...prevState,
				subMaterId: dataSubMatter.data,
			}))
		}
	}, [isSuccessSubMatter, dataSubMatter])

	useEffect(() => {
		if (isSuccessClearHistoric) {
			GetDashboard({
				disciplineIds: '',
				matterIds: '',
				submatterIds: '',
				yearIds: '',
				positionIds: '',
				juryIds: '',
				institutionIds: '',
				dateInterval: interval.dateInterval.value,
			})
			dispatch(notificationCreators.show(successMessages.clearHistoric))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [GetDashboard, dispatch, isSuccessClearHistoric])

	const verifyPhoneModal =
		cookiesStorage.get('phoneModalPostpone') ||
		cookiesStorage.get('closePhoneModal')

	useEffect(() => {
		if (userInfo && !verifyPhoneModal && !userInfo?.phone) {
			dispatch(
				modalCreators.openModal({
					name: 'editPhone',
					modalInfo: userInfo,
				})
			)
		}
	}, [verifyPhoneModal, dispatch, userInfo])

	return (
		<div className={container()}>
			<div>
				<div className={title()}>
					<Title variant='h1' color='$neutral160'>
						Dashboard
					</Title>
					{mobile ? (
						<div className={buttons()}>
							<FilterButton
								onClick={() => {
									setOpenMenuFilter(true)
								}}
								css={{
									maxWidth: '204px',
									gridArea: 'filter',
									'@md': { width: '100%', maxWidth: '204px' },
								}}
							>
								Filtro de Desempenho
							</FilterButton>

							<ClearButton
								onClick={() => {
									setOpenModalClear(true)
								}}
								css={{
									maxWidth: '180px',
									gridArea: 'clear',
									'@md': { width: '100%', maxWidth: '189px' },
								}}
							>
								Limpar histórico
							</ClearButton>
							<RefreshButton
								onClick={handleUpdateDash}
								css={{
									gridArea: 'refresh',
									'@md': {
										width: '100%',
										maxWidth: '135px',
										p: { display: 'block' },
									},
								}}
							>
								Atualizar
							</RefreshButton>
						</div>
					) : (
						<div className={buttons()}>
							<ClearButton
								onClick={() => {
									setOpenModalClear(true)
								}}
								css={{
									maxWidth: '180px',
									'@md': { width: '100%', maxWidth: '189px' },
								}}
							>
								Limpar histórico
							</ClearButton>
							<RefreshButton
								onClick={handleUpdateDash}
								css={{
									width: '56px',
									p: { display: 'none' },
									'@md': {
										width: '100%',
										maxWidth: '135px',
										p: { display: 'block' },
									},
								}}
							>
								Atualizar
							</RefreshButton>
							<FilterButton
								onClick={() => {
									setOpenMenuFilter(true)
								}}
								css={{
									maxWidth: '204px',
									'@md': { width: '100%', maxWidth: '204px' },
								}}
							>
								Filtro de Desempenho
							</FilterButton>
						</div>
					)}
				</div>
			</div>
			<div className={listCardsStats()}>
				<CardStats
					value={dashValues.answered}
					desc='Questões resolvidas'
					type='total'
					mobile={mobile}
				/>
				<CardStats
					value={dashValues.totalAnswered}
					desc='Questões únicas resolvidas'
					mobile={mobile}
				/>
				<CardStats
					value={dashValues.correct}
					desc='Respostas corretas'
					type='correct'
					mobile={mobile}
				/>
				<CardStats
					value={dashValues.errors}
					desc='Respostas erradas'
					type='wrong'
					mobile={mobile}
				/>
				<CardStats
					value={`${
						dashValues.correct / dashValues.answered
							? ((dashValues.correct / dashValues.answered) * 100).toFixed(0)
							: 0
					}%`}
					desc='Media geral'
					type='average'
					mobile={mobile}
				/>
			</div>
			<ChartsHistoric data={dashValues.history} />
			<div className={listCharts()}>
				<TableHistoric
					data={dashValues.lastMockResolutions.map((item) => {
						return {
							name: item.mockExam.category.name,
							week: item.mockExam.name,
							date: moment(item.created_at).format('DD/MM/YYYY'),
							value: item.points,
							time: item.conclusion_time,
						}
					})}
				/>
				<ChartDoughnut
					hits={dashValues.correct}
					errors={dashValues.errors}
					css={{
						height: 'auto',
						maxWidth: '200%',
						'@md': {
							maxWidth: '363px',
						},
					}}
				/>
			</div>

			<SideModalBase
				name='Filtro de Desempenho'
				open={openMenufilter}
				setOpen={setOpenMenuFilter}
			>
				<>
					<div className={filterSideMenu()}>
						<AutocompleteBase
							name='disciplines'
							onChange={setValues}
							placeholder='Código/Lei'
							selectedText='Código/Lei'
							options={initOptions.disciplines}
							selectedValue={values.disciplines}
						/>
						<AutocompleteBase
							name='materId'
							onChange={setValues}
							placeholder='Assunto'
							selectedText='Assunto'
							options={initOptions.materId}
							selectedValue={values.materId}
						/>
						<AutocompleteBase
							name='subMaterId'
							onChange={setValues}
							placeholder='SubAssunto'
							selectedText='SubAssunto'
							options={initOptions.subMaterId}
							selectedValue={values.subMaterId}
						/>
						<AutocompleteBase
							name='office'
							onChange={setValues}
							placeholder='Cargo'
							selectedText='Cargo'
							options={initOptions.roles}
							selectedValue={values.office}
						/>
						<AutocompleteBase
							name='juries'
							onChange={setValues}
							placeholder='Banca'
							selectedText='Banca'
							options={initOptions.juries}
							selectedValue={values.juries}
						/>
						<SelectBase
							name='dateInterval'
							onChange={setInterval}
							placeholder='Período'
							options={[
								{
									label: 'Período',
									value: 'all',
								},
								{
									label: 'Hoje',
									value: 'today',
								},
								{
									label: '1 dia atrás',
									value: 'oneDay',
								},
								{
									label: '7 dias atrás',
									value: 'week',
								},
								{
									label: '14 dias atrás',
									value: 'halfMonth',
								},
								{
									label: '30 dias atrás',
									value: 'month',
								},
							]}
							selectedValue={interval.dateInterval}
						/>
					</div>

					<div className={tagsSideMenu()}>
						<TagsActions
							title='Código/Lei'
							name='disciplines'
							options={values.disciplines}
							onClose={setValues}
						/>
						<TagsActions
							title='Assunto'
							name='materId'
							options={values.materId}
							onClose={setValues}
						/>
						<TagsActions
							title='SubAssunto'
							name='subMaterId'
							options={values.subMaterId}
							onClose={setValues}
						/>
						<TagsActions
							title='Cargo'
							name='office'
							options={values.office}
							onClose={setValues}
						/>
						<TagsActions
							title='Banca'
							name='juries'
							options={values.juries}
							onClose={setValues}
						/>
						<TagsActions
							title='Ano'
							name='year'
							options={values.year}
							onClose={setValues}
						/>
					</div>
					<div className={buttonSideMenu()}>
						<Button onClick={handleUpdateDash}>Aplicar filtro</Button>
						<Button outlined onClick={handleClearFilter}>
							Limpar filtro
						</Button>
					</div>
				</>
			</SideModalBase>

			<ModalBase
				title=''
				open={openModalClear}
				onOpenChange={setOpenModalClear}
				maxWidth='609px'
			>
				<div className={deleteHistoryContainer()}>
					<img
						src='/assets/images/deleteHistoryBG.jpg'
						alt='Limpar Histórico img'
						className={deleteHistoryImage()}
					/>
					<h1 className={deleteHistoryTitle()}>Deseja apagar histórico?</h1>
					<div
						style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
					>
						<Paragraph
							style={{ marginTop: '12px', color: 'black' }}
							className={deleteHistoryParaghaph()}
						>
							Deseja apagar todas as questões resolvidas em questionário geral,
							disciplina, caderno e simulados?
						</Paragraph>
						<h5
							style={{ marginTop: '12px', color: 'red' }}
							className={deleteHistoryParaghaph()}
						>
							Atenção:
						</h5>
						<p
							style={{ marginTop: '2px', color: '#909496' }}
							className={deleteHistoryParaghaph()}
						>
							Isso também irá apagar seus simulados realizados no formato prova!
						</p>
						<input
							type='text'
							placeholder='Escreva "Apagar histórico" e confirme'
							value={inputValue}
							onChange={handleInputChange}
							className={deleteHistoryInputText()}
						/>
						<div style={{ display: 'flex', gap: '26px', marginTop: '20px' }}>
							<button
								type='button'
								style={{ color: 'white' }}
								className={deleteHistoryButton()}
								onClick={() => {
									handleClear()
									setOpenModalClear(false)
								}}
								disabled={isButtonDisabled}
							>
								Sim, apagar
							</button>
							<button
								type='button'
								style={{
									color: '#909496',
									backgroundColor: 'white',
									border: '1px solid #909496',
								}}
								className={deleteHistoryButton()}
								onClick={() => {
									setOpenModalClear(false)
								}}
							>
								Não, cancelar
							</button>
						</div>
					</div>
				</div>
			</ModalBase>
		</div>
	)
}
